import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Printer } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TagView = () => {
  const { ticketId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicket(response.data);
    } catch (error) {
      toast.error('Failed to fetch ticket');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return <div className="text-center text-slate-500">Ticket not found</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => navigate(`/tickets/${ticketId}`)} data-testid="back-to-details-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Details
        </Button>
        <Button onClick={handlePrint} className="bg-indigo-600 hover:bg-indigo-700" data-testid="print-tag-btn">
          <Printer className="w-4 h-4 mr-2" />
          Print Tag
        </Button>
      </div>

      {/* Printable Tag Display */}
      <div ref={printRef} className="print-tag">
        <Card className="border-4 border-slate-900 max-w-2xl mx-auto">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-8 border-b-4 border-slate-900 pb-6">
              <img
                src="/logo.png"
                alt="JustFold"
                className="h-16 mx-auto mb-2 object-contain"
              />
              <p className="text-xl font-medium text-slate-600">Laundry Service Tag</p>
            </div>

            {/* Ticket Number - Large & Prominent */}
            <div className="text-center mb-8 bg-indigo-50 border-2 border-indigo-600 rounded-lg py-6">
              <p className="text-sm font-semibold text-indigo-600 mb-2">ORDER NUMBER</p>
              <p className="text-4xl font-bold font-mono text-indigo-900" data-testid="tag-ticket-number">{ticket.ticket_number}</p>
            </div>

            {/* Customer & Service Details */}
            <div className="grid grid-cols-2 gap-6 mb-8 text-lg">
              <div className="border-2 border-slate-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-slate-500 mb-2">CUSTOMER NAME</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="tag-customer-name">{ticket.customer_name}</p>
              </div>
              <div className="border-2 border-slate-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-slate-500 mb-2">SERVICE TYPE</p>
                <p className="text-2xl font-bold text-slate-900 uppercase" data-testid="tag-service-type">{ticket.service_type}</p>
              </div>
            </div>

            {/* Weight & Quantity */}
            <div className="grid grid-cols-2 gap-6 mb-8 text-lg">
              <div className="bg-slate-100 border-2 border-slate-300 rounded-lg p-4 text-center">
                <p className="text-sm font-semibold text-slate-500 mb-2">TOTAL WEIGHT</p>
                <p className="text-4xl font-bold text-slate-900" data-testid="tag-weight">{ticket.total_kg || ticket.kg || 0}</p>
                <p className="text-lg font-medium text-slate-600">KG</p>
              </div>
              <div className="bg-slate-100 border-2 border-slate-300 rounded-lg p-4 text-center">
                <p className="text-sm font-semibold text-slate-500 mb-2">TOTAL CLOTHES</p>
                <p className="text-4xl font-bold text-slate-900" data-testid="tag-clothes-count">{ticket.clothes_count || ticket.items?.length || 0}</p>
                <p className="text-lg font-medium text-slate-600">PIECES</p>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="text-sm font-semibold text-slate-500 mb-1">PICKUP DATE</p>
                <p className="text-xl font-bold text-slate-900" data-testid="tag-pickup-date">
                  {ticket.pickup_date ? format(new Date(ticket.pickup_date), 'dd MMM yyyy') : 'N/A'}
                </p>
                {ticket.pickup_date && (
                  <p className="text-sm text-slate-600">
                    {format(new Date(ticket.pickup_date), 'EEEE, hh:mm a')}
                  </p>
                )}
              </div>
              {ticket.delivery_date && (
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm font-semibold text-slate-500 mb-1">DELIVERY DATE</p>
                  <p className="text-xl font-bold text-slate-900" data-testid="tag-delivery-date">
                    {format(new Date(ticket.delivery_date), 'dd MMM yyyy')}
                  </p>
                  <p className="text-sm text-slate-600">
                    {format(new Date(ticket.delivery_date), 'EEEE, hh:mm a')}
                  </p>
                </div>
              )}
            </div>

            {/* Status & Amount */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4 text-center">
                <p className="text-sm font-semibold text-blue-600 mb-2">STATUS</p>
                <p className="text-2xl font-bold text-blue-900 uppercase" data-testid="tag-status">{ticket.status}</p>
              </div>
              <div className="bg-indigo-100 border-2 border-indigo-600 rounded-lg p-4 text-center">
                <p className="text-sm font-semibold text-indigo-600 mb-2">TOTAL AMOUNT</p>
                <p className="text-3xl font-bold text-indigo-900" data-testid="tag-total-amount">₹{(ticket.total_amount || ticket.total_price || 0).toFixed(2)}</p>
              </div>
            </div>

            {/* Special Instructions */}
            {ticket.special_instructions && (
              <div className="mb-8 p-5 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                <p className="text-sm font-semibold text-slate-700 mb-2">SPECIAL INSTRUCTIONS</p>
                <p className="text-base text-slate-900" data-testid="tag-instructions">{ticket.special_instructions}</p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 pt-6 border-t-2 border-slate-200 text-center text-xs text-slate-400">
              <p>Thank you for choosing JustFold</p>
              <p className="mt-1">Generated on {format(new Date(), 'MMMM dd, yyyy')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TagView;
